import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Plus, ListChecks, Clock, MessageCircle, Camera, Printer, ShoppingCart, HelpCircle, ChefHat, Flame } from 'lucide-react';

// Events to notify RecipeBox when help tiles should be shown/hidden
export const SHOW_HELP_TILES_EVENT = 'showHelpTiles';
export const HIDE_NEW_RECIPE_TIP_EVENT = 'hideNewRecipeTip';
export const SCROLL_TO_TOP_EVENT = 'scrollToTop';

const HowHoneyWorks = () => {
  // Check if first-time user and set initial state
  const hasSeenGuide = typeof window !== 'undefined' 
    ? localStorage.getItem('hasSeenHowHoneyWorks') 
    : true;
  const [isExpanded, setIsExpanded] = useState(!hasSeenGuide);
  
  // Mark as seen when collapsed
  useEffect(() => {
    if (!isExpanded && !localStorage.getItem('hasSeenHowHoneyWorks')) {
      localStorage.setItem('hasSeenHowHoneyWorks', 'true');
    }
  }, [isExpanded]);

  // Detect if mobile
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  // When expanded, dispatch event to show help tiles
  const handleToggle = () => {
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);
    
    if (newExpanded) {
      // When opening the guide, show all help tiles
      window.dispatchEvent(new CustomEvent(SHOW_HELP_TILES_EVENT));
    } else {
      // When closing the guide, hide the New Recipe tip and scroll to top
      window.dispatchEvent(new CustomEvent(HIDE_NEW_RECIPE_TIP_EVENT));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const steps = [
    {
      number: 1,
      title: "Add recipes to your Recipe Box",
      icon: <Plus className="w-5 h-5 text-amber-600" />,
      description: "Add recipes using AI chat or a URL. Adjust ingredients, steps, or portions anytime.",
      visual: (
        <div className="flex items-center gap-2 mt-2 text-sm text-gray-600">
          <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-xs font-medium">+ New Recipe</span>
        </div>
      )
    },
    {
      number: 2,
      title: "Then plan what's next",
      icon: <ListChecks className="w-5 h-5 text-blue-600" />,
      description: (<>Tap <strong>Up Next</strong> on any recipe to line up what you plan to cook.</>),
      subtext: (<>Honey combines ingredients from all your Up Next recipes into one <strong>aisle-sorted shopping list</strong>.</>),
      visual: (
        <div className="flex items-center gap-2 mt-2 text-sm text-gray-600">
          <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-medium">Up Next</span>
        </div>
      )
    },
    {
      number: 3,
      title: "Start cooking",
      icon: <ChefHat className="w-5 h-5 text-red-500" />,
      description: (<>Tap <strong>Cook</strong> to pin recipes you're actively making to the top of your Recipe Box.</>),
      subtext: "Cook one recipe—or several at once.",
      bullets: [
        { icon: <Clock size={12} />, text: "Concurrent cooking" },
        { icon: <Printer size={12} />, text: "Multi-recipe printing" }
      ],
      visual: (
        <div className="flex items-center gap-2 mt-2 text-sm text-gray-600">
          <span className="bg-red-100 text-red-600 px-2 py-0.5 rounded text-xs font-medium">Cook</span>
        </div>
      )
    },
    {
      number: 4,
      title: "Get help as you cook",
      icon: <MessageCircle className="w-5 h-5 text-purple-600" />,
      description: "Need to make a change or ask a question?",
      subtext: "Use the chat icon on any recipe for real-time advice and adjustments.",
      example: "\"Can I swap chicken thighs for breasts and adjust the cook time?\"",
      visual: (
        <div className="flex items-center gap-2 mt-2 text-sm">
          <MessageCircle size={16} className="text-purple-500" />
        </div>
      )
    },
    {
      number: 5,
      title: "Finish & remember",
      icon: <Camera className="w-5 h-5 text-green-600" />,
      description: "Mark the recipe as cooked, add a photo, or share a note.",
      subtext: "Buzz privately to your cooking journal—or share with a hive.",
      visual: (
        <div className="flex items-center gap-2 mt-2 text-sm text-gray-600">
          <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs font-medium">Cooked ✓</span>
        </div>
      )
    }
  ];

  return (
    <div className="mb-4">
      {/* Single unified container */}
      <div className={`bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg overflow-hidden transition-all ${isExpanded ? 'shadow-sm' : ''}`}>
        {/* Clickable Header */}
        <button
          onClick={handleToggle}
          className="w-full px-4 py-3 flex items-center justify-between hover:from-amber-100 hover:to-orange-100 transition-all"
          data-testid="how-honey-works-toggle"
        >
          <div className="flex items-center gap-2">
            <span className="text-xl">🍯</span>
            <span className="font-medium text-amber-800">How to Use Honey</span>
            <span className="text-xs text-amber-600 hidden sm:inline">(a recipe for success)</span>
          </div>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-amber-600" />
          ) : (
            <ChevronDown className="w-5 h-5 text-amber-600" />
          )}
        </button>

        {/* Expanded Content - inside same container */}
        {isExpanded && (
          <div className="border-t border-amber-200">
            {/* Recipe Header */}
            <div className="bg-gradient-to-r from-amber-100 to-orange-100 px-4 py-3 border-b border-amber-200">
              <h2 className="font-semibold text-amber-900 flex items-center gap-2">
                <span className="text-lg">📖</span>
                Recipe: Getting Started with Honey
              </h2>
              <p className="text-sm text-amber-700 mt-1">Your cooking journey in 5 simple steps</p>
            </div>

            {/* Steps - Recipe Instructions Style */}
            <div className="divide-y divide-amber-100 bg-white">
              {steps.map((step) => (
              <div key={step.number} className="px-4 py-4 hover:bg-amber-50/50 transition-colors">
                <div className="flex gap-3">
                  {/* Step Number */}
                  <div className="flex-shrink-0 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                    {step.number}
                  </div>
                  
                  {/* Step Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {step.icon}
                      <h3 className="font-semibold text-gray-900">{step.title}</h3>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">{step.description}</p>
                    
                    {/* Subtext if any */}
                    {step.subtext && (
                      <p className="text-sm text-gray-500 mt-1 leading-relaxed">{step.subtext}</p>
                    )}
                    
                    {/* Bullet points if any */}
                    {step.bullets && (
                      <ul className="mt-2 space-y-1">
                        {step.bullets.map((bullet, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-xs text-gray-600">
                            <span className="text-amber-600">{bullet.icon}</span>
                            {bullet.text}
                          </li>
                        ))}
                      </ul>
                    )}
                    
                    {/* Example quote if any */}
                    {step.example && (
                      <p className="mt-2 text-xs text-gray-500 italic bg-gray-50 px-2 py-1 rounded">
                        {step.example}
                      </p>
                    )}
                    
                    {step.visual}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="bg-amber-50 px-4 py-3 border-t border-amber-200">
            <button 
              onClick={handleToggle}
              className="w-full text-center text-sm text-amber-700 hover:text-amber-800 font-medium"
            >
              Got it! Let's cook 🍳
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

// Help tile component for Up Next section
export const UpNextHelpTile = ({ viewMode, onDismiss }) => {
  if (viewMode === 'grid') {
    // Card view - fuller content
    return (
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-dashed border-blue-300 rounded-lg p-4 relative min-h-[160px] flex flex-col">
        <button 
          onClick={onDismiss}
          className="absolute top-2 right-2 text-blue-400 hover:text-blue-600 transition-colors"
          aria-label="Dismiss help"
        >
          ✕
        </button>
        <div className="flex items-center gap-2 mb-2">
          <HelpCircle className="w-5 h-5 text-blue-500" />
          <span className="font-semibold text-blue-800">Up Next</span>
        </div>
        <p className="text-sm text-blue-700 mb-2">
          Recipes you're planning to cook soon.
        </p>
        <ul className="text-xs text-blue-600 space-y-1 mb-3">
          <li className="flex items-center gap-1.5">
            <ShoppingCart size={12} /> Interactive shopping lists
          </li>
          <li className="flex items-center gap-1.5">
            <ListChecks size={12} /> Easy meal planning
          </li>
        </ul>
        <p className="text-xs text-blue-500 italic mt-auto text-right">
          Tap Up Next on any recipe to add it here
        </p>
      </div>
    );
  }
  
  // List view - compact with instruction text
  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-dashed border-blue-300 rounded-lg px-3 py-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-blue-700">
          <HelpCircle size={16} className="text-blue-500 flex-shrink-0" />
          <span><strong>Up Next</strong> = your meal plan lineup</span>
        </div>
        <button 
          onClick={onDismiss}
          className="text-blue-400 hover:text-blue-600 transition-colors ml-2"
          aria-label="Dismiss help"
        >
          ✕
        </button>
      </div>
      <p className="text-xs text-blue-500 italic mt-1 ml-6">
        Tap Up Next on any recipe to add it here
      </p>
    </div>
  );
};

// Help tile component for Currently Cooking section
export const CookingHelpTile = ({ viewMode, onDismiss }) => {
  if (viewMode === 'grid') {
    // Card view - fuller content
    return (
      <div className="bg-gradient-to-br from-amber-50 to-yellow-50 border-2 border-dashed border-amber-300 rounded-lg p-4 relative min-h-[160px] flex flex-col">
        <button 
          onClick={onDismiss}
          className="absolute top-2 right-2 text-amber-400 hover:text-amber-600 transition-colors"
          aria-label="Dismiss help"
        >
          ✕
        </button>
        <div className="flex items-center gap-2 mb-2">
          <HelpCircle className="w-5 h-5 text-amber-600" />
          <span className="font-semibold text-amber-800">Currently Cooking</span>
        </div>
        <p className="text-sm text-amber-700 mb-2">
          Recipes in progress, anchored to top for easy access.
        </p>
        <ul className="text-xs text-amber-600 space-y-1 mb-3">
          <li className="flex items-center gap-1.5">
            <Flame size={12} /> Concurrent cooking support
          </li>
          <li className="flex items-center gap-1.5">
            <Printer size={12} /> Multi-recipe print
          </li>
        </ul>
        <p className="text-xs text-amber-600 italic mt-auto text-right">
          Tap Cook on any recipe to add it here
        </p>
      </div>
    );
  }
  
  // List view - compact with instruction text
  return (
    <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-dashed border-amber-300 rounded-lg px-3 py-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-amber-700">
          <HelpCircle size={16} className="text-amber-600 flex-shrink-0" />
          <span><strong>Cooking:</strong> In progress recipes, anchored to the top</span>
        </div>
        <button 
          onClick={onDismiss}
          className="text-amber-400 hover:text-amber-600 transition-colors ml-2"
          aria-label="Dismiss help"
        >
          ✕
        </button>
      </div>
      <p className="text-xs text-amber-600 italic mt-1 ml-6">
        Tap Cook on any recipe to add it here
      </p>
    </div>
  );
};

export default HowHoneyWorks;
